import React, { useState, useRef, useEffect } from 'react';
import { User, Users, Camera, Trash2, Save, LogOut, CheckCircle2 } from 'lucide-react';
import { db, auth, storage } from '../../firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getIntegrationsStatus,
  initiateIntegrationConnection,
  disconnectIntegrationConnection,
  getIntegrationDisplayName,
  isIntegrationConfigured
} from '../../services/integrationService';
import {
  getUserIntegrationsStatus,
  initiateUserIntegrationConnection,
  disconnectUserIntegration,
  getIntegrationDisplayName as getUserIntegrationDisplayName,
  isIntegrationConfigured as isUserIntegrationConfigured
} from '../../services/userIntegrationService';
import {
  connectFathomWithApiKey,
  disconnectFathom,
} from '../../services/fathomIntegrationService';
import {
  saveUserNotificationPreferences,
  getNotificationDisplayName
} from '../../services/notificationService';
import {
  buildNotificationsForInternalProfile,
  getAllowedNotificationKeysForInternalUser,
  isFullInternalNotificationRole,
} from '../../utils/notificationInternalPolicy';
import PortalBackground from '../../components/PortalBackground';
import McpTokensSection from '../../components/crm/McpTokensSection';

// Shared visual styles — same design language as the portal profile page.
const inputBase = 'w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';
const labelBase = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5';
const sectionCard = 'rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 mb-6';

const SectionHeading = ({ emoji, children }) => (
  <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-6">
    <span className="text-lg leading-none">{emoji}</span>
    {children}
  </h2>
);

const Toggle = ({ checked, onChange, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 ${
      checked ? 'bg-red-600' : 'bg-gray-200 dark:bg-gray-700'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-[22px]' : 'translate-x-0.5'
      }`}
    />
  </button>
);

const IntegrationRow = ({ logo, name, description, subtext, connected, connecting, onConnect, onDisconnect }) => (
  <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
    <div className="flex items-center gap-4 min-w-0">
      <img
        src={logo}
        alt={name}
        className="w-8 h-8 object-contain shrink-0"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{name}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
        {subtext && (
          <p className="text-xs mt-0.5 text-gray-400 dark:text-gray-500">{subtext}</p>
        )}
      </div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {connected ? (
        <>
          <button
            type="button"
            onClick={onDisconnect}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            Disconnect
          </button>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Connected
          </span>
        </>
      ) : (
        <button
          type="button"
          onClick={onConnect}
          disabled={connecting}
          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
        >
          {connecting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Connecting...
            </>
          ) : (
            'Connect'
          )}
        </button>
      )}
    </div>
  </div>
);

const CRM_NOTIFICATION_TILES = [
  { key: 'newLead', title: 'New Lead', emoji: '🧲', tile: 'bg-blue-50 dark:bg-blue-900/30' },
  { key: 'newClient', title: 'New Client', emoji: '🤝', tile: 'bg-green-50 dark:bg-green-900/30' },
  { key: 'newMessage', title: 'New Message', emoji: '📩', tile: 'bg-purple-50 dark:bg-purple-900/30' },
  { key: 'newQuestion', title: 'New Question', emoji: '❓', tile: 'bg-amber-50 dark:bg-amber-900/30' },
  { key: 'newFire', title: 'New Fire', emoji: '🔥', tile: 'bg-red-50 dark:bg-red-900/30' },
  { key: 'newAnnouncement', title: 'New Announcement', emoji: '📢', tile: 'bg-orange-50 dark:bg-orange-900/30' },
  { key: 'newTask', title: 'New Task', emoji: '✅', tile: 'bg-indigo-50 dark:bg-indigo-900/30' },
  { key: 'technologyFulfillment', title: 'Technology Fulfillment', emoji: '🤖', tile: 'bg-cyan-50 dark:bg-cyan-900/30' },
  { key: 'hrFulfillment', title: 'HR Fulfillment', emoji: '👥', tile: 'bg-pink-50 dark:bg-pink-900/30' },
  { key: 'marketingFulfillment', title: 'Marketing Fulfillment', emoji: '📣', tile: 'bg-teal-50 dark:bg-teal-900/30' },
  { key: 'generalFulfillment', title: 'General Fulfillment', emoji: '📋', tile: 'bg-slate-100 dark:bg-slate-800' },
  { key: 'communityPost', title: 'Community Post', emoji: '💬', tile: 'bg-emerald-50 dark:bg-emerald-900/30' },
  { key: 'ndaSigned', title: 'NDA Signed', emoji: '✍️', tile: 'bg-violet-50 dark:bg-violet-900/30' },
  { key: 'contractSigned', title: 'Contract Signed', emoji: '📄', tile: 'bg-amber-50 dark:bg-amber-900/30' },
  { key: 'portalSignup', title: 'Portal Signup', emoji: '🔑', tile: 'bg-rose-50 dark:bg-rose-900/30' },
  { key: 'summitRegistration', title: 'Summit Registration', emoji: '📅', tile: 'bg-lime-50 dark:bg-lime-900/30' },
  { key: 'flaggedLead', title: 'Flagged Lead', emoji: '🚩', tile: 'bg-yellow-50 dark:bg-yellow-900/30' },
  { key: 'assessmentComplete', title: 'Assessment Complete', emoji: '🎯', tile: 'bg-emerald-50 dark:bg-emerald-900/30' },
  { key: 'clientCheckInComplete', title: 'Check In', emoji: '⭐', tile: 'bg-amber-50 dark:bg-amber-900/30' },
];

const CRMProfile = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const canManageOtherUsers = (role) => role === 'Owner';
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    jobTitle: '',
    timeZone: 'America/New_York',
    notifications: {
      newLead: false,
      newClient: false,
      newMessage: false,
      newQuestion: false,
      newFire: false,
      newAnnouncement: false,
      newTask: false,
      technologyFulfillment: false,
      hrFulfillment: false,
      marketingFulfillment: false,
      generalFulfillment: false,
      communityPost: false,
      ndaSigned: false,
      contractSigned: false,
      portalSignup: false,
      summitRegistration: false,
      flaggedLead: false,
      assessmentComplete: false,
      clientCheckInComplete: false
    },
    language: 'English',
    country: 'United States',
    timeFormat: '12 h',
    role: ''
  });

  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState({ type: '', text: '' });

  // Integration states
  const [integrationsStatus, setIntegrationsStatus] = useState({});
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [connectingIntegration, setConnectingIntegration] = useState(null);
  const [showFathomKeyModal, setShowFathomKeyModal] = useState(false);
  const [fathomApiKeyDraft, setFathomApiKeyDraft] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          console.log("No authenticated user found");
          return;
        }

        console.log("Fetching CRM profile for user:", user.uid);
        console.log("User display name:", user.displayName);
        console.log("User email:", user.email);
        
        const userDoc = doc(db, "internal_users", user.uid);
        const docSnap = await getDoc(userDoc);

        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log("Fetched CRM profile data:", data);
          
          // Merge existing data with Google account data to ensure all fields are populated
          const mergedData = {
            firstName: data.firstName || user.displayName?.split(' ')[0] || '',
            lastName: data.lastName || user.displayName?.split(' ')[1] || '',
            email: data.email || user.email || '',
            phone: data.phone || user.phoneNumber || '',
            jobTitle: data.jobTitle || '',
            timeZone: data.timeZone || 'America/New_York',
            notifications: buildNotificationsForInternalProfile(
              data.role || '',
              data.team || '',
              data.notifications || {}
            ),
            language: data.language || 'English',
            country: data.country || 'United States',
            timeFormat: data.timeFormat || '12 h',
            role: data.role || '',
            team: data.team || '',
            createdAt: data.createdAt || new Date().toISOString(),
            lastUpdated: new Date().toISOString()
          };
          
          console.log("Merged CRM profile data:", mergedData);
          setProfileData(mergedData);
          
          if (data.profileImage) {
            setProfileImage(data.profileImage);
          }
          
          // Update the document with merged data if there were missing fields
          if (JSON.stringify(data) !== JSON.stringify(mergedData)) {
            console.log("Updating CRM profile with merged data");
            await updateDoc(userDoc, mergedData);
          }
        } else {
          console.log("No user data found for ID:", user.uid);
          // Create initial user document
          const initialUserData = {
            firstName: user.displayName?.split(' ')[0] || '',
            lastName: user.displayName?.split(' ')[1] || '',
            email: user.email || '',
            phone: user.phoneNumber || '',
            jobTitle: '',
            timeZone: 'America/New_York',
            notifications: buildNotificationsForInternalProfile('', '', {}),
            language: 'English',
            country: 'United States',
            timeFormat: '12 h',
            role: '',
            team: '',
            createdAt: new Date().toISOString(),
            lastUpdated: new Date().toISOString()
          };
          
          console.log("Creating initial CRM user data:", initialUserData);
          await setDoc(userDoc, initialUserData);
          setProfileData(initialUserData);
        }
      } catch (error) {
        console.error("Error fetching CRM profile data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchIntegrationsStatus = async () => {
      try {
        setIntegrationsLoading(true);
        const user = auth.currentUser;
        if (user) {
          // Get user-level integrations (Google Calendar, Gmail)
          const userStatus = await getUserIntegrationsStatus(user.uid);
          
          // Get account-level integrations (QuickBooks) - force server read to bypass cache (critical after OAuth callback)
          const accountStatus = await getIntegrationsStatus({ forceRefresh: true });
          
          // Merge statuses - QuickBooks + DocuSign from account-level, others from user-level
          setIntegrationsStatus({
            ...userStatus,
            quickbooks: accountStatus.quickbooks || { connected: false },
            docusign: accountStatus.docusign || { connected: false }
          });
        }
      } catch (error) {
        console.error('Error fetching integrations status:', error);
      } finally {
        setIntegrationsLoading(false);
      }
    };

    fetchProfile();
    fetchIntegrationsStatus();
  }, []);

  const refreshIntegrationsStatus = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const userStatus = await getUserIntegrationsStatus(user.uid);
      const accountStatus = await getIntegrationsStatus({ forceRefresh: true });
      setIntegrationsStatus({
        ...userStatus,
        quickbooks: accountStatus.quickbooks || { connected: false },
        docusign: accountStatus.docusign || { connected: false }
      });
    } catch (e) {
      console.warn('refreshIntegrationsStatus', e);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value
    });
  };

  const handleNotificationToggle = async (notificationKey) => {
    if (!isFullInternalNotificationRole(profileData.role || '')) {
      const allowed = getAllowedNotificationKeysForInternalUser(profileData.role, profileData.team);
      if (!allowed.has(notificationKey)) return;
    }
    const newValue = !profileData.notifications[notificationKey];
    
    // Update local state immediately for UI responsiveness
    const updatedNotifications = {
      ...profileData.notifications,
      [notificationKey]: newValue
    };
    const normalizedNotifications = buildNotificationsForInternalProfile(
      profileData.role || '',
      profileData.team || '',
      updatedNotifications
    );
    
    setProfileData({
      ...profileData,
      notifications: normalizedNotifications
    });

    // Save to Firestore
    try {
      const user = auth.currentUser;
      if (user) {
        await saveUserNotificationPreferences(user.uid, normalizedNotifications);
        
        setUpdateMessage({
          type: 'success',
          text: `${getNotificationDisplayName(notificationKey)} notification ${newValue ? 'enabled' : 'disabled'} successfully!`
        });
      }
    } catch (error) {
      console.error('Error saving notification preference:', error);
      setUpdateMessage({
        type: 'error',
        text: `Failed to save ${getNotificationDisplayName(notificationKey)} preference. Please try again.`
      });
      
      // Revert the local state on error
      setProfileData({
        ...profileData,
        notifications: buildNotificationsForInternalProfile(
          profileData.role || '',
          profileData.team || '',
          {
            ...profileData.notifications,
            [notificationKey]: !newValue
          }
        )
      });
    }
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setUpdateMessage({
          type: 'error',
          text: 'Please select an image file.'
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setUpdateMessage({
          type: 'error',
          text: 'Image size should be less than 5MB.'
        });
        return;
      }

      try {
        const user = auth.currentUser;
        if (!user) {
          setUpdateMessage({
            type: 'error',
            text: 'Please log in to update your profile picture.'
          });
          return;
        }

        // Upload to Firebase Storage
        const storageRef = ref(storage, `profilePictures/${user.uid}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        // Update state and Firestore
        setProfileImage(downloadURL);
        const userDoc = doc(db, "internal_users", user.uid);
        await updateDoc(userDoc, {
          profileImage: downloadURL,
          lastUpdated: new Date().toISOString()
        });

        setUpdateMessage({
          type: 'success',
          text: 'Profile picture updated successfully!'
        });
      } catch (error) {
        console.error('Error updating profile picture:', error);
        setUpdateMessage({
          type: 'error',
          text: 'Failed to update profile picture. Please try again.'
        });
      }
    }
  };

  const handleDeleteImage = async (e) => {
    e.stopPropagation();
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Delete from Firebase Storage
      const storageRef = ref(storage, `profilePictures/${user.uid}`);
      await deleteObject(storageRef);

      // Update state and Firestore
      setProfileImage(null);
      const userDoc = doc(db, "internal_users", user.uid);
      await updateDoc(userDoc, {
        profileImage: null
      });
    } catch (error) {
      console.error("Error deleting profile image:", error);
      setUpdateMessage({
        type: 'error',
        text: 'Error deleting profile picture. Please try again.'
      });
    }
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    setUpdateMessage({ type: '', text: '' });

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user found');
      }

      const userDoc = doc(db, "internal_users", user.uid);
      await updateDoc(userDoc, {
        ...profileData,
        lastUpdated: new Date().toISOString()
      });

      setUpdateMessage({
        type: 'success',
        text: 'CRM Profile updated successfully!'
      });
    } catch (error) {
      console.error("Error updating CRM profile:", error);
      setUpdateMessage({
        type: 'error',
        text: error.message || 'Error updating profile. Please try again.'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Explicitly navigate to login page
      navigate('/login', { replace: true });
    } catch (error) {
      console.error("Error signing out:", error);
      setUpdateMessage({
        type: 'error',
        text: 'Error signing out. Please try again.'
      });
    }
  };


  const handleConnectIntegration = async (integrationKey) => {
    try {
      setConnectingIntegration(integrationKey);

      if (integrationKey === 'fathom') {
        setFathomApiKeyDraft('');
        setShowFathomKeyModal(true);
        return;
      }

      // QuickBooks and DocuSign use account-level integration (for Owner/Executive)
      if (integrationKey === 'quickbooks' || integrationKey === 'docusign') {
        if (!isIntegrationConfigured(integrationKey)) {
          setUpdateMessage({
            type: 'error',
            text: `${getIntegrationDisplayName(integrationKey)} is not configured. Please contact your administrator.`
          });
          return;
        }
        
        // Use account-level integration service (no userId in state)
        await initiateIntegrationConnection(integrationKey);
      } else {
        // Other integrations use user-level integration
        if (!isUserIntegrationConfigured(integrationKey)) {
          setUpdateMessage({
            type: 'error',
            text: `${getUserIntegrationDisplayName(integrationKey)} is not configured. Please contact your administrator.`
          });
          return;
        }

        await initiateUserIntegrationConnection(integrationKey);
      }
    } catch (error) {
      console.error('Error connecting integration:', error);
      const displayName = integrationKey === 'quickbooks' 
        ? getIntegrationDisplayName(integrationKey)
        : getUserIntegrationDisplayName(integrationKey);
      setUpdateMessage({
        type: 'error',
        text: `Failed to connect ${displayName}: ${error.message}`
      });
    } finally {
      setConnectingIntegration(null);
    }
  };

  const handleDisconnectIntegration = async (integrationKey) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setUpdateMessage({
          type: 'error',
          text: 'Please log in to disconnect integrations.'
        });
        return;
      }

      // QuickBooks and DocuSign use account-level integration
      if (integrationKey === 'quickbooks' || integrationKey === 'docusign') {
        await disconnectIntegrationConnection(integrationKey);
        // Refresh integrations status
        const accountStatus = await getIntegrationsStatus();
        const userStatus = await getUserIntegrationsStatus(user.uid);
        setIntegrationsStatus({
          ...userStatus,
          quickbooks: accountStatus.quickbooks || { connected: false },
          docusign: accountStatus.docusign || { connected: false }
        });
        setUpdateMessage({
          type: 'success',
          text: `${getIntegrationDisplayName(integrationKey)} disconnected successfully!`
        });
      } else if (integrationKey === 'fathom') {
        await disconnectFathom();
        await refreshIntegrationsStatus();
        setUpdateMessage({
          type: 'success',
          text: `${getUserIntegrationDisplayName(integrationKey)} disconnected successfully!`
        });
      } else {
        // Other integrations use user-level integration
        await disconnectUserIntegration(user.uid, integrationKey);
        // Refresh integrations status
        const userStatus = await getUserIntegrationsStatus(user.uid);
        const accountStatus = await getIntegrationsStatus();
        setIntegrationsStatus({
          ...userStatus,
          quickbooks: accountStatus.quickbooks || { connected: false },
          docusign: accountStatus.docusign || { connected: false }
        });
        setUpdateMessage({
          type: 'success',
          text: `${getUserIntegrationDisplayName(integrationKey)} disconnected successfully!`
        });
      }
    } catch (error) {
      console.error('Error disconnecting integration:', error);
      const displayName = (integrationKey === 'quickbooks' || integrationKey === 'docusign')
        ? getIntegrationDisplayName(integrationKey)
        : getUserIntegrationDisplayName(integrationKey);
      setUpdateMessage({
        type: 'error',
        text: `Failed to disconnect ${displayName}: ${error.message}`
      });
    }
  };

  const handleSubmitFathomApiKey = async () => {
    try {
      setConnectingIntegration('fathom');
      await connectFathomWithApiKey(fathomApiKeyDraft);
      setShowFathomKeyModal(false);
      setFathomApiKeyDraft('');
      await refreshIntegrationsStatus();
      setUpdateMessage({
        type: 'success',
        text: 'Fathom connected. Meeting summaries will sync when recordings finish.',
      });
    } catch (error) {
      console.error('Fathom connect failed:', error);
      setUpdateMessage({
        type: 'error',
        text: error.message || 'Failed to connect Fathom',
      });
    } finally {
      setConnectingIntegration(null);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen relative w-full overflow-x-hidden px-4 py-6 md:p-8">
        {/* Same ambient BBA background the portal profile shows */}
        <PortalBackground />
        <div className="relative max-w-4xl mx-auto w-full">
          <div className="text-center mb-6 animate-pulse">
            <div className="h-7 w-40 mx-auto rounded bg-gray-100 dark:bg-gray-800"></div>
            <div className="h-3 w-64 mx-auto mt-2 rounded bg-gray-100 dark:bg-gray-800"></div>
          </div>
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 mb-6 animate-pulse">
              <div className="h-4 w-44 rounded bg-gray-100 dark:bg-gray-800 mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[0, 1, 2, 3].map((j) => (
                  <div key={j} className="space-y-2">
                    <div className="h-3 w-24 rounded bg-gray-100 dark:bg-gray-800"></div>
                    <div className="h-10 rounded-lg bg-gray-100 dark:bg-gray-800"></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const isExecutiveOrOwner = profileData.role === 'Executive' || profileData.role === 'Owner';

  return (
    <div className="min-h-screen relative w-full overflow-x-hidden px-4 py-6 md:p-8">
      {/* Same ambient BBA background the portal profile shows */}
      <PortalBackground />
      <div className="relative max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="relative mb-6">
          <div className="text-center px-14 md:px-32 py-1">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Profile</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage your personal info, preferences, and integrations
            </p>
          </div>
        </div>

        {/* Update Message */}
        {updateMessage.text && (
          <div className={`mb-6 p-4 rounded-xl border text-sm font-medium ${
            updateMessage.type === 'success'
              ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800'
              : 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800'
          }`}>
            {updateMessage.text}
          </div>
        )}

        {/* My Information */}
        <div className={sectionCard}>
          <SectionHeading emoji="👤">
            My Information
          </SectionHeading>

          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            {/* Profile Picture */}
            <div className="flex flex-col items-center gap-2 shrink-0">
              <div
                className="relative w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden cursor-pointer ring-2 ring-gray-200 dark:ring-gray-700 hover:ring-red-300 dark:hover:ring-red-700 transition-all group"
                onClick={handleImageClick}
              >
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-full h-full text-gray-300 dark:text-gray-600 p-4" />
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera className="w-5 h-5 text-white" />
                </div>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageChange}
                accept="image/*"
                className="hidden"
              />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Profile Picture</span>
              {profileImage && (
                <button
                  className="inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors"
                  onClick={handleDeleteImage}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove
                </button>
              )}
            </div>

            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First name */}
              <div>
                <label className={labelBase}>First name</label>
                <input
                  type="text"
                  name="firstName"
                  value={profileData.firstName}
                  onChange={handleInputChange}
                  className={inputBase}
                  placeholder="Enter your first name"
                />
              </div>

              {/* Last name */}
              <div>
                <label className={labelBase}>Last name</label>
                <input
                  type="text"
                  name="lastName"
                  value={profileData.lastName}
                  onChange={handleInputChange}
                  className={inputBase}
                  placeholder="Enter your last name"
                />
              </div>

              {/* Job title */}
              <div>
                <label className={labelBase}>Job title</label>
                <input
                  type="text"
                  name="jobTitle"
                  value={profileData.jobTitle}
                  onChange={handleInputChange}
                  className={inputBase}
                  placeholder="Enter your job title"
                />
              </div>

              {/* Phone */}
              <div>
                <label className={labelBase}>Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleInputChange}
                  className={inputBase}
                  placeholder="Enter your phone number"
                />
              </div>

              {/* Business email */}
              <div>
                <label className={labelBase}>Business email</label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  disabled
                  placeholder="Email address"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 text-sm text-gray-500 dark:text-gray-400 cursor-not-allowed focus:outline-none"
                />
              </div>

              {/* Time zone */}
              <div>
                <label className={labelBase}>Time zone</label>
                <select
                  name="timeZone"
                  value={profileData.timeZone}
                  onChange={handleInputChange}
                  className={inputBase}
                >
                  <option value="America/New_York">America/New_York</option>
                  <option value="America/Chicago">America/Chicago</option>
                  <option value="America/Denver">America/Denver</option>
                  <option value="America/Los_Angeles">America/Los_Angeles</option>
                </select>
              </div>
            </div>
          </div>

          {/* Team + Role */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
            <div className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
              <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-lg shrink-0">
                🏷️
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Team</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {profileData.team || 'No team assigned'}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center text-lg shrink-0">
                  🎖️
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Role</p>
                  <p className={`text-sm font-semibold truncate ${
                    profileData.role === 'Owner' ? 'text-yellow-600 dark:text-yellow-400' :
                    profileData.role === 'Executive' ? 'text-red-600 dark:text-red-400' :
                    profileData.role === 'Contractor' ? 'text-purple-600 dark:text-purple-400' :
                    profileData.role === 'Accountant' ? 'text-green-600 dark:text-green-400' :
                    'text-gray-600 dark:text-gray-300'
                  }`}>
                    {profileData.role || 'No role assigned'}
                  </p>
                </div>
              </div>
              {canManageOtherUsers(profileData.role) && (
                <button
                  type="button"
                  onClick={() => navigate('/crm/internal-users')}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold shrink-0 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <Users className="w-3.5 h-3.5" />
                  Manage others
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className={sectionCard}>
          <SectionHeading emoji="🔔">
            Notifications
          </SectionHeading>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {CRM_NOTIFICATION_TILES.filter((tile) => {
              if (isFullInternalNotificationRole(profileData.role || '')) return true;
              const allowed = getAllowedNotificationKeysForInternalUser(profileData.role, profileData.team);
              return allowed.has(tile.key);
            }).map(({ key, title, emoji, tile }) => {
              const enabled = !!profileData.notifications[key];
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-800"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-lg ${tile}`}>
                      {emoji}
                    </div>
                    <p className={`text-sm font-medium truncate ${
                      enabled ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {title}
                    </p>
                  </div>
                  <Toggle
                    checked={enabled}
                    onChange={() => handleNotificationToggle(key)}
                    ariaLabel={`Toggle ${title} notifications`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Integrations */}
        <div id="integrations" className={`${sectionCard} scroll-mt-24`}>
          <SectionHeading emoji="🔌">
            Integrations
          </SectionHeading>
          <div className="space-y-3">
            <IntegrationRow
              logo="/gcal-icon.png"
              name="Google Calendar"
              description="Connect your Google Calendar to sync your meetings and events"
              connected={!!integrationsStatus.googleCalendar?.connected}
              connecting={connectingIntegration === 'googleCalendar'}
              onConnect={() => handleConnectIntegration('googleCalendar')}
              onDisconnect={() => handleDisconnectIntegration('googleCalendar')}
            />

            <IntegrationRow
              logo="/gmail-icon.png"
              name="Gmail"
              description="Connect your Gmail account to sync your emails and contacts"
              connected={!!integrationsStatus.gmail?.connected}
              connecting={connectingIntegration === 'gmail'}
              onConnect={() => handleConnectIntegration('gmail')}
              onDisconnect={() => handleDisconnectIntegration('gmail')}
            />

            <IntegrationRow
              logo="/fathomlogo.png"
              name="Fathom"
              description="Connect your Fathom to sync meeting records"
              connected={!!integrationsStatus.fathom?.connected}
              connecting={connectingIntegration === 'fathom'}
              onConnect={() => handleConnectIntegration('fathom')}
              onDisconnect={() => handleDisconnectIntegration('fathom')}
            />

            <IntegrationRow
              logo="/calendly-logo.png"
              name="Calendly"
              description="Connect Calendly to sync strategy call bookings and meeting lifecycle"
              subtext={integrationsStatus.calendly?.connected ? integrationsStatus.calendly?.email : null}
              connected={!!integrationsStatus.calendly?.connected}
              connecting={connectingIntegration === 'calendly'}
              onConnect={() => handleConnectIntegration('calendly')}
              onDisconnect={() => handleDisconnectIntegration('calendly')}
            />

            {isExecutiveOrOwner && (
              <IntegrationRow
                logo="/qbo.png"
                name="QuickBooks"
                description="Connect your QuickBooks account to sync financial data"
                connected={!!integrationsStatus.quickbooks?.connected}
                connecting={connectingIntegration === 'quickbooks'}
                onConnect={() => handleConnectIntegration('quickbooks')}
                onDisconnect={() => handleDisconnectIntegration('quickbooks')}
              />
            )}

            {isExecutiveOrOwner && (
              <IntegrationRow
                logo="/docusignlogo.png"
                name="DocuSign"
                description="Connect your DocuSign account to send and manage NDA envelopes"
                subtext={integrationsStatus.docusign?.connected ? integrationsStatus.docusign?.authData?.email : null}
                connected={!!integrationsStatus.docusign?.connected}
                connecting={connectingIntegration === 'docusign'}
                onConnect={() => handleConnectIntegration('docusign')}
                onDisconnect={() => handleDisconnectIntegration('docusign')}
              />
            )}
          </div>
        </div>

        {/* Claude / MCP access tokens — Owner only */}
        {profileData.role === 'Owner' && <McpTokensSection />}

        {/* Style Preferences */}
        <div className={sectionCard}>
          <SectionHeading emoji="🎨">
            Style Preferences
          </SectionHeading>
          <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-lg shrink-0">
                🌙
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Dark Mode</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Switch the CRM to a darker color palette</p>
              </div>
            </div>
            <Toggle checked={darkMode} onChange={toggleDarkMode} ariaLabel="Toggle dark mode" />
          </div>
        </div>

        {/* Save / Log out */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
          <button
            onClick={handleUpdate}
            disabled={isUpdating}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
          >
            {isUpdating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save
              </>
            )}
          </button>
        </div>

        {/* Fathom API Key Modal */}
        {showFathomKeyModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-md w-full p-6">
              <div className="flex flex-col items-center text-center mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-2 text-lg">
                  🎥
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Connect Fathom</h3>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Go to{' '}
                <a
                  href="https://fathom.video/customize"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:text-blue-700 underline"
                >
                  Fathom
                </a>
                , then Settings → API Access → Add → Generate Api Key → Copy Key → Paste here.
              </p>
              <div className="mb-4">
                <label className={labelBase}>Fathom API key</label>
                <input
                  type="password"
                  autoComplete="off"
                  value={fathomApiKeyDraft}
                  onChange={(e) => setFathomApiKeyDraft(e.target.value)}
                  placeholder="Fathom API key"
                  className={inputBase}
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowFathomKeyModal(false);
                    setFathomApiKeyDraft('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!fathomApiKeyDraft.trim() || connectingIntegration === 'fathom'}
                  onClick={handleSubmitFathomApiKey}
                  className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed transition-colors"
                >
                  Save &amp; register webhook
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CRMProfile;
